import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format, isValid } from 'date-fns';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebaseClient';
import EnhancedTable from 'components/dashboard/EnhancedTable';
import StatusBadge from 'components/dashboard/StatusBadge';
import ActionButtons from 'components/dashboard/ActionButtons';
import GlassyModal from 'components/ui/GlassyModal';
import ClassEditModal from 'components/modals/ClassEditModal';
import AdminClassCreationForm from 'components/modals/AdminClassCreationForm';
import EditableCell from 'components/dashboard/EditableCell';
import FloatingActionButton from 'components/ui/FloatingActionButton';
import Icon from 'components/AppIcon';
import { useClasses } from 'hooks/useClasses';
import { useProfiles } from 'hooks/useProfiles';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';

const EnhancedClassesTable = () => {
  const { classes, loading, updateStatus, deleteClass, updateClass } = useClasses();
  const { profiles } = useProfiles();
  const { isAdmin, profile, user } = useAuth();
  const [selectedClass, setSelectedClass] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Helper function to get writer name from ID
  const getWriterName = (writerId) => {
    if (!writerId) return null;
    const writer = profiles.find(p => p.id === writerId);
    return writer ? writer.full_name : 'Unknown Writer';
  };

  // Helper function to get client name from ID
  const getClientName = (clientId) => {
    if (!clientId) return null;
    const client = profiles.find(p => p.id === clientId);
    return client ? client.full_name : 'Unknown Client';
  };

  // Enrich classes with client names for mobile cards
  const enrichedClasses = useMemo(() => {
    return classes.map(classItem => ({
      ...classItem,
      client_name: getClientName(classItem.clientId)
    }));
  }, [classes, profiles]);

  // Helper function to handle field updates
  const handleFieldUpdate = async (classId, field, value) => {
    try {
      // Get old value for history tracking
      const classItem = classes.find(c => c.id === classId);
      const oldValue = classItem ? classItem[field] : null;
      
      // Clean and validate the value
      let cleanValue = value;
      
      // Handle empty strings
      if (cleanValue === '') {
        cleanValue = null;
      }
      
      // Handle specific field types
      if (field === 'start_date' || field === 'end_date') {
        cleanValue = cleanValue ? new Date(cleanValue) : null;
      }
      
      console.log('Updating class:', { classId, field, value: cleanValue });
      await updateClass(classId, { [field]: cleanValue });
      
      // Track edit history
      if (oldValue !== cleanValue) {
        try {
          console.log('Saving class edit history:', {
            item_id: classId,
            item_type: 'class',
            field: field,
            old_value: oldValue || '',
            new_value: cleanValue || '',
            editor_id: user?.uid,
            editor_name: profile?.full_name || user?.email,
            editor_role: profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'user'
          });
          
          await addDoc(collection(db, 'edit_history'), {
            item_id: classId,
            item_type: 'class',
            field: field,
            old_value: oldValue || '',
            new_value: cleanValue || '',
            editor_id: user?.uid,
            editor_name: profile?.full_name || user?.email,
            editor_role: profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'user',
            created_at: serverTimestamp()
          });
          
          console.log('Class edit history saved successfully');
        } catch (historyError) {
          console.error('Failed to save class edit history:', historyError);
        }
      }
      
      toast.success('Class updated successfully');
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error('Failed to update class');
      throw error;
    }
  };

  // Status options for dropdowns
  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'active', label: 'Active' }
  ];

  // Dynamic status filter options based on actual data
  const statusFilterOptions = useMemo(() => {
    const uniqueStatuses = [...new Set(classes.map(classItem => classItem.status).filter(Boolean))];
    const statusLabels = {
      'pending': 'Pending',
      'in-progress': 'In Progress',
      'completed': 'Completed', 
      'cancelled': 'Cancelled',
      'scheduled': 'Scheduled',
      'active': 'Active'
    };
    
    return uniqueStatuses.map(status => ({
      value: status,
      label: statusLabels[status] || status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [classes]);

  // Communication options
  const communicationOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'discord', label: 'Discord' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'zoom', label: 'Zoom' },
    { value: 'teams', label: 'Microsoft Teams' }
  ];

  // Writer options
  const writerOptions = profiles
    .filter(p => p.is_writer)
    .map(writer => ({
      value: writer.id,
      label: `${writer.full_name} (${writer.email})`
    }));


  const columns = useMemo(
    () => [
      {
        accessorKey: 'class_title',
        header: 'Class Title',
        cell: ({ row }) => (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-secondary/20 rounded-lg flex items-center justify-center">
              <Icon name="BookOpen" size={16} className="text-secondary" />
            </div>
            <div className="flex-1">
              <EditableCell
                value={row.original.class_title}
                onSave={(value) => handleFieldUpdate(row.original.id, 'class_title', value)}
                placeholder="Class title"
                isEditable={isAdmin()}
                className="font-medium"
              />
              <EditableCell
                value={row.original.subject}
                onSave={(value) => handleFieldUpdate(row.original.id, 'subject', value)}
                placeholder="Subject"
                isEditable={isAdmin()}
                className="text-sm text-white/60"
              />
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'class_forum',
        header: 'Forum',
        cell: ({ row }) => (
          <EditableCell
            value={row.original.class_forum}
            onSave={(value) => handleFieldUpdate(row.original.id, 'class_forum', value)}
            placeholder="Forum"
            isEditable={isAdmin()}
            className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-sm"
          />
        ),
      },
      {
        accessorKey: 'start_date',
        header: 'Start Date',
        cell: ({ row }) => {
          const startDate = row.original.start_date;
          const dateValue = startDate && isValid(startDate) ? startDate.toISOString().split('T')[0] : '';
          const displayValue = startDate && isValid(startDate) ? format(startDate, 'MMM dd, yyyy') : 'Not set';
          
          return (
            <EditableCell
              value={dateValue}
              onSave={(value) => handleFieldUpdate(row.original.id, 'start_date', value ? new Date(value) : null)}
              placeholder="Not set"
              type="date"
              isEditable={isAdmin()}
              displayValue={displayValue}
            />
          );
        },
      },
      {
        accessorKey: 'end_date',
        header: 'End Date',
        cell: ({ row }) => {
          const endDate = row.original.end_date;
          const dateValue = endDate && isValid(endDate) ? endDate.toISOString().split('T')[0] : '';
          const displayValue = endDate && isValid(endDate) ? format(endDate, 'MMM dd, yyyy') : 'Not set';
          
          return (
            <EditableCell
              value={dateValue}
              onSave={(value) => handleFieldUpdate(row.original.id, 'end_date', value ? new Date(value) : null)}
              placeholder="Not set"
              type="date"
              isEditable={isAdmin()}
              displayValue={displayValue}
            />
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          isAdmin() ? (
            <EditableCell
              value={row.original.status}
              onSave={(value) => handleFieldUpdate(row.original.id, 'status', value)}
              type="select"
              options={statusOptions}
              isEditable={isAdmin()}
              displayValue={<StatusBadge status={row.original.status} />}
            />
          ) : (
            <StatusBadge status={row.original.status} />
          )
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => {
          const createdAt = row.original.created_at;
          const displayValue = createdAt && isValid(createdAt) ? format(createdAt, 'MMM dd, yyyy') : 'Unknown';
          
          return (
            <span className="text-white/80">{displayValue}</span>
          );
        },
      },
    ],
    [isAdmin, handleFieldUpdate, statusOptions, communicationOptions, writerOptions]
  );

  const handleViewClass = (classItem) => {
    setSelectedClass(classItem);
    setShowDetailsModal(true);
  };

  const handleEditClass = (classItem) => {
    setSelectedClass(classItem);
    setShowEditModal(true);
  };

  const handleDeleteClass = async (id) => {
    try {
      await deleteClass(id);
    } catch (error) {
      // Error already handled in the hook
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateStatus(id, status);
    } catch (error) {
      // Error already handled in the hook
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    // Classes will automatically refresh due to real-time listeners
  };

  const ClassDetailsModal = () => (
    <GlassyModal
      isOpen={showDetailsModal}
      onClose={() => setShowDetailsModal(false)}
      title="Class Details"
      icon="BookOpen"
      maxWidth="max-w-3xl"
    >
      {selectedClass && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-white">
                {selectedClass.class_title}
              </h3>
              <p className="text-white/60">{selectedClass.subject}</p>
            </div>
            <div className="flex items-center space-x-3">
              <StatusBadge status={selectedClass.status} size="lg" />
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleEditClass(selectedClass);
                }}
                className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 hover:text-green-300 rounded-lg transition-colors flex items-center space-x-2"
              >
                <Icon name="Edit" size={16} />
                <span>Edit</span>
              </button>
            </div>
          </div>

          {/* Class Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">Class Information</h4>
              <div className="space-y-1 text-sm">
                <p><span className="text-white/60">Forum:</span> {selectedClass.class_forum}</p>
                <p><span className="text-white/60">Subject:</span> {selectedClass.subject}</p>
                <p><span className="text-white/60">Status:</span> {selectedClass.status}</p>
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="font-medium text-white mb-2">Schedule</h4>
              <div className="space-y-1 text-sm">
                <p><span className="text-white/60">Start Date:</span> {
                  (() => {
                    const date = selectedClass.start_date && typeof selectedClass.start_date.toDate === 'function' 
                      ? selectedClass.start_date.toDate() 
                      : selectedClass.start_date;
                    return isValid(date) ? format(date, 'MMM dd, yyyy') : 'Not set';
                  })()
                }</p>
                <p><span className="text-white/60">End Date:</span> {
                  (() => {
                    const date = selectedClass.end_date && typeof selectedClass.end_date.toDate === 'function'
                      ? selectedClass.end_date.toDate()
                      : selectedClass.end_date;
                    return isValid(date) ? format(date, 'MMM dd, yyyy') : 'Not set';
                  })()
                }</p>
                <p><span className="text-white/60">Created:</span> {
                  (() => {
                    const date = selectedClass.created_at && typeof selectedClass.created_at.toDate === 'function'
                      ? selectedClass.created_at.toDate()
                      : selectedClass.created_at;
                    return isValid(date) ? format(date, 'MMM dd, yyyy') : 'Unknown';
                  })()
                }</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Description</h4>
            <EditableCell
              value={selectedClass.description || ''}
              onSave={(value) => handleFieldUpdate(selectedClass.id, 'description', value)}
              placeholder="No description"
              multiline={true}
              isEditable={isAdmin()}
              className="text-white/80 text-sm whitespace-pre-wrap"
            />
          </div>

          {/* Additional Notes */}
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Notes</h4>
            <EditableCell
              value={selectedClass.notes || ''}
              onSave={(value) => handleFieldUpdate(selectedClass.id, 'notes', value)}
              placeholder="No notes"
              multiline={true}
              isEditable={isAdmin()}
              className="text-white/80 text-sm whitespace-pre-wrap"
            />
          </div>

          {/* Meeting Link */}
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Meeting Link</h4>
            <EditableCell
              value={selectedClass.meeting_link || ''}
              onSave={(value) => handleFieldUpdate(selectedClass.id, 'meeting_link', value)}
              placeholder="No meeting link"
              type="url"
              isEditable={isAdmin()}
              displayValue={selectedClass.meeting_link ? (
                <a
                  href={selectedClass.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 text-sm flex items-center space-x-2"
                >
                  <Icon name="ExternalLink" size={16} />
                  <span>Join Class</span>
                </a>
              ) : (
                'No meeting link'
              )}
              className="text-white/80 text-sm"
            />
          </div>

          {/* Writer Assignment */}
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Assigned Writer</h4>
            <EditableCell
              value={selectedClass.assigned_writer || ''}
              onSave={(value) => handleFieldUpdate(selectedClass.id, 'assigned_writer', value)}
              type="select"
              options={writerOptions}
              isEditable={isAdmin()}
              placeholder="No writer assigned"
              displayValue={getWriterName(selectedClass.assigned_writer) || 'No writer assigned'}
              className="text-white/80 text-sm"
            />
          </div>
        </div>
      )}
    </GlassyModal>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-secondary/20 rounded-lg flex items-center justify-center">
            <Icon name="GraduationCap" size={20} className="text-currentColor" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Class Management</h2>
            <p className="text-text-muted text-sm">
              Manage and track all classes
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-secondary hover:bg-secondary/80 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <Icon name="Plus" size={16} />
          <span>Create New Class</span>
        </button>
      </div>

      {/* Outer transparent glass container */}
      <div className="bg-transparent backdrop-blur-none border-none rounded-xl p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total', value: classes.length, icon: 'BookOpen', color: 'blue' },
            { label: 'Pending', value: classes.filter(c => c.status === 'pending').length, icon: 'Clock', color: 'yellow' },
            { label: 'In Progress', value: classes.filter(c => c.status === 'in-progress').length, icon: 'Play', color: 'blue' },
            { label: 'Completed', value: classes.filter(c => c.status === 'completed').length, icon: 'CheckCircle', color: 'green' },
          ].filter(stat => stat.label === 'Total' || stat.value > 0).map((stat) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-4 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">{stat.label}</p>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 bg-${stat.color}-500/20 rounded-lg flex items-center justify-center`}>
                  <Icon name={stat.icon} size={24} className={`text-${stat.color}-400`} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Inner container for table section */}
        <div className="p-6 rounded-xl bg-transparent border-none shadow-none">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-white mb-2">Class Management</h2>
            <p className="text-white/60 text-sm">Manage and track all classes in real-time</p>
          </div>
          
          <EnhancedTable
            data={enrichedClasses}
            columns={columns}
            loading={loading}
            onRowClick={handleViewClass}
            searchPlaceholder="Search classes..."
            emptyMessage="No classes found"
            mobileCardLayout={true}
            mobileColumns={['class_title', 'level', 'deadline', 'client_name']}
            responsiveBreakpoint="lg"
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            statusFilterOptions={statusFilterOptions}
            useDropdownFiltersOnMobile={true}
            mobileActions={[
              {
                label: 'View Details',
                icon: 'Eye',
                onClick: handleViewClass,
                className: 'bg-primary/30 hover:bg-primary/40 text-primary'
              },
              {
                label: 'Edit',
                icon: 'Edit',
                onClick: handleEditClass,
                className: 'bg-green-500/30 hover:bg-green-500/40 text-green-400'
              },
              {
                label: 'Delete',
                icon: 'Trash2',
                onClick: (classItem) => {
                  if (window.confirm('Are you sure you want to delete this class?')) {
                    handleDeleteClass(classItem.id);
                  }
                },
                className: 'bg-red-500/30 hover:bg-red-500/40 text-red-400'
              }
            ]}
          />
        </div>
      </div>

      {/* Details Modal */}
      <ClassDetailsModal />
      
      {/* Edit Modal */}
      <ClassEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        classItem={selectedClass}
        onSave={updateClass}
      />

      {/* Create Modal */}
      <AdminClassCreationForm
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSuccess}
      />

      {/* Mobile FAB */}
      <FloatingActionButton
        onClick={() => setShowCreateModal(true)}
        icon="Plus"
        label="Create Class"
        position="bottom-right"
        size="md"
        showOnMobile={true}
        showOnDesktop={false}
      />
    </div>
  );
};

export default EnhancedClassesTable;